/**
 * Event time helper - converts event times to user's local timezone using native Intl API.
 * No external dependencies (no Moment, spacetime, etc).
 */

function guessUserTz() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return "America/Los_Angeles";
  }
}

/**
 * Format date as "Mon. February 26" (weekday. month day) in user's timezone.
 * @param {string} dateStr - ISO 8601 date string from Craft datetime('c')
 * @param {string} [userTz] - IANA timezone; defaults to guessUserTz()
 */
function formatDay(dateStr, userTz) {
  const tz = userTz || guessUserTz();
  const date = new Date(dateStr);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "short",
    month: "long",
    day: "numeric",
  }).formatToParts(date);
  const weekday = parts.find((p) => p.type === "weekday").value;
  const month = parts.find((p) => p.type === "month").value;
  const day = parts.find((p) => p.type === "day").value;
  return `${weekday}. ${month} ${day}`.toUpperCase();
}

/**
 * Format date as "Mon. February 26" (title case, for event detail page).
 * @param {string} dateStr - ISO 8601 date string from Craft datetime('c')
 * @param {string} [userTz] - IANA timezone; defaults to guessUserTz()
 */
function formatDayTitle(dateStr, userTz) {
  const tz = userTz || guessUserTz();
  const date = new Date(dateStr);
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "short",
    month: "long",
    day: "numeric",
  }).formatToParts(date);
  const weekday = parts.find((p) => p.type === "weekday").value;
  const month = parts.find((p) => p.type === "month").value;
  const day = parts.find((p) => p.type === "day").value;
  return `${weekday}. ${month} ${day}`;
}

/**
 * Format time as "11:30am" in user's timezone (lowercase am/pm, no space).
 * @param {string} dateStr - ISO 8601 date string from Craft datetime('c')
 * @param {string} [userTz] - IANA timezone; defaults to guessUserTz()
 */
function formatTime(dateStr, userTz) {
  const tz = userTz || guessUserTz();
  const date = new Date(dateStr);
  const formatted = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date);
  return formatted.replace(/\s(AM|PM)$/i, (m) => m.trim().toLowerCase());
}

/**
 * Format date as "Wednesday, February 26" (long weekday, for featured events etc).
 * @param {string} dateStr - ISO 8601 date string from Craft datetime('c')
 * @param {string} [userTz] - IANA timezone; defaults to guessUserTz()
 */
function formatDayLong(dateStr, userTz) {
  const tz = userTz || guessUserTz();
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "long",
    month: "long",
    day: "numeric",
  }).format(date);
}

/**
 * Format date as "February 26, 2026" (for past events).
 * @param {string} dateStr - ISO 8601 date string from Craft datetime('c')
 * @param {string} [userTz] - IANA timezone; defaults to guessUserTz()
 */
function formatDayPast(dateStr, userTz) {
  const tz = userTz || guessUserTz();
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(date);
}

/**
 * Get timezone abbreviation (e.g. PST, EST) for user's timezone.
 * @param {string} tzName - IANA timezone (e.g. "America/Los_Angeles")
 * @returns {string} Abbreviation (e.g. "PST", "EST") or ""
 */
function getTimezoneAbbr(tzName) {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tzName,
      timeZoneName: "short",
    }).formatToParts(new Date());
    const tzPart = parts.find((p) => p.type === "timeZoneName");
    return tzPart?.value || "";
  } catch {
    return "";
  }
}

/**
 * Update all event cards on the page with user-local date/time.
 *
 * Expected HTML structure:
 * - Container: [data-date-start] (ISO string), optional [data-date-end], [data-date-format]
 * - Day: .event__day
 * - Time: .event__time
 * - Optional split: .event__start-time, .event__end-time, .event__tz
 * - Past events: .event__day-past
 *
 * data-date-format: "day" (short, uppercase) | "day-title" (short, title case) | "day-long" | "day-past"
 *
 * @listens DOMContentLoaded
 * @listens htmx:afterSwap
 * @listens sprig:render
 */
function updateEventTimes() {
  if (!window.eventTimeHelper) return;
  const h = window.eventTimeHelper;
  const tz = h.guessUserTz();
  const tzAbbr = h.getTimezoneAbbr(tz);

  document.querySelectorAll("[data-date-start]").forEach((el) => {
    const start = el.getAttribute("data-date-start");
    const end = el.getAttribute("data-date-end");
    const format = el.getAttribute("data-date-format") || "day-long";
    if (!start) return;

    const dayEl = el.querySelector(".event__day");
    const timeEl = el.querySelector(".event__time");
    const dayPastEl = el.querySelector(".event__day-past");
    const startTimeEl = el.querySelector(".event__start-time");
    const endTimeEl = el.querySelector(".event__end-time");
    const tzEl = el.querySelector(".event__tz");

    // themeEvent / events/_entry: same-day vs multi-day when we have dayEl + timeEl (no startTimeEl/endTimeEl)
    // Same-day: "Wed" + "2:00pm - 4:00pm EST"; multi-day: "Wed 2:00pm - Thu 4:00pm EST" (all in dayEl, timeEl empty)
    const isThemeEventFormat = dayEl && timeEl && !startTimeEl && !endTimeEl;
    if (isThemeEventFormat) {
      const dayFormat = el.getAttribute("data-date-format") || "day-long";
      const formatDayFn = dayFormat === "day" ? h.formatDay : dayFormat === "day-title" ? h.formatDayTitle : h.formatDayLong;
      const uDay = formatDayFn(start, tz);
      const uStartTime = h.formatTime(start, tz);
      let uEndTime = "";
      if (end) uEndTime = h.formatTime(end, tz);
      const uEndDayFmt = end ? formatDayFn(end, tz) : uDay;
      if (end && uDay !== uEndDayFmt) {
        dayEl.textContent = uDay + " " + uStartTime + " - " + uEndDayFmt + " " + uEndTime + (tzAbbr ? " " + tzAbbr : "");
        timeEl.textContent = "";
      } else {
        dayEl.textContent = uDay;
        timeEl.textContent = uStartTime + (uEndTime ? " - " + uEndTime : "") + (tzAbbr ? " " + tzAbbr : "");
      }
      return;
    }

    const formatDayFn = format === "day-past" ? h.formatDayPast : format === "day" ? h.formatDay : format === "day-title" ? h.formatDayTitle : h.formatDayLong;

    if (dayEl) dayEl.textContent = formatDayFn(start, tz);
    if (dayPastEl) {
      const uDayPast = h.formatDayPast(start, tz);
      dayPastEl.textContent = end ? (uDayPast === h.formatDayPast(end, tz) ? uDayPast : uDayPast + " - " + h.formatDayPast(end, tz)) : uDayPast;
    }

    if (startTimeEl) startTimeEl.textContent = h.formatTime(start, tz);
    if (endTimeEl && end) endTimeEl.textContent = h.formatTime(end, tz);
    if (tzEl) tzEl.textContent = tzAbbr;

    if (timeEl) {
      let timeText = h.formatTime(start, tz);
      if (end) timeText += " - " + h.formatTime(end, tz);
      timeEl.textContent = timeText + (tzAbbr ? " " + tzAbbr : "");
    }
  });
}

// Expose on window for global access (e.g. manual updateEventTimes calls after dynamic content)
window.eventTimeHelper = {
  guessUserTz,
  formatDay,
  formatDayTitle,
  formatDayLong,
  formatTime,
  formatDayPast,
  getTimezoneAbbr,
  updateEventTimes,
};

window.updateEventTimes = updateEventTimes;

if (typeof document !== "undefined") {
  // Re-run when HTMX or Sprig injects new content (e.g. Load More, filters)
  document.addEventListener("htmx:afterSwap", updateEventTimes);
  document.addEventListener("sprig:render", updateEventTimes);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", updateEventTimes);
  } else {
    updateEventTimes();
  }
}

export { guessUserTz, formatDay, formatDayTitle, formatDayLong, formatTime, formatDayPast, getTimezoneAbbr, updateEventTimes };
